import { useCallback, useEffect, useMemo, useState } from "react";
import type { ScheduleEntry, ScheduleOverride } from "../lib/realestate";
import { useDeleteScheduleOverride, useUpsertScheduleOverride } from "./realestate";
import {
  type DiffAction,
  type DraftRow,
  computeAction,
  initDraftRowsMap,
  validateDraftRow,
} from "./useScheduleOverrideBulkEdit.helpers";

interface UseScheduleOverrideBulkEditOptions {
  propertyId: string;
  loanId: string;
  schedule: ScheduleEntry[];
  overrides: ScheduleOverride[];
}

type SetRowPatch = Partial<Pick<DraftRow, "interestCents" | "principalCents" | "notes">>;

interface SaveState {
  loading: boolean;
  rowErrors: Map<string, string>;
  lastSaveAttemptCount: number;
}

export interface UseScheduleOverrideBulkEditResult {
  editMode: boolean;
  enterEditMode: () => void;
  cancelEdit: () => void;
  confirmDiscard: () => void;
  abortDiscardConfirmation: () => void;
  needsDiscardConfirmation: boolean;
  rows: Map<string, DraftRow>;
  setRow: (month: string, patch: SetRowPatch) => void;
  resetRow: (month: string) => void;
  changesCount: number;
  save: () => Promise<void>;
  saveState: SaveState;
}

function findOverride(yearMonth: string, overrides: ScheduleOverride[]): ScheduleOverride | null {
  return overrides.find((o) => o.month.slice(0, 7) === yearMonth) ?? null;
}

function findEntry(yearMonth: string, schedule: ScheduleEntry[]): ScheduleEntry | null {
  return schedule.find((e) => e.date.slice(0, 7) === yearMonth) ?? null;
}

export function useScheduleOverrideBulkEdit(
  options: UseScheduleOverrideBulkEditOptions,
): UseScheduleOverrideBulkEditResult {
  const { propertyId, loanId, schedule, overrides } = options;
  const upsertMutation = useUpsertScheduleOverride();
  const deleteMutation = useDeleteScheduleOverride();

  const [editMode, setEditMode] = useState(false);
  const [rows, setRows] = useState<Map<string, DraftRow>>(new Map());
  const [needsDiscardConfirmation, setNeedsDiscardConfirmation] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>({
    loading: false,
    rowErrors: new Map(),
    lastSaveAttemptCount: 0,
  });

  const changesCount = useMemo(() => {
    let count = 0;
    for (const draft of rows.values()) {
      const ym = draft.month.slice(0, 7);
      const entry = findEntry(ym, schedule);
      if (!entry) continue;
      const existing = findOverride(ym, overrides);
      if (computeAction(draft, entry, existing) !== "NOOP") count += 1;
    }
    return count;
  }, [rows, schedule, overrides]);

  const enterEditMode = useCallback(() => {
    setRows(initDraftRowsMap(schedule, overrides));
    setSaveState({ loading: false, rowErrors: new Map(), lastSaveAttemptCount: 0 });
    setNeedsDiscardConfirmation(false);
    setEditMode(true);
  }, [schedule, overrides]);

  const exitEditMode = useCallback(() => {
    setEditMode(false);
    setRows(new Map());
    setNeedsDiscardConfirmation(false);
    setSaveState({ loading: false, rowErrors: new Map(), lastSaveAttemptCount: 0 });
  }, []);

  const cancelEdit = useCallback(() => {
    if (changesCount > 0) {
      setNeedsDiscardConfirmation(true);
      return;
    }
    exitEditMode();
  }, [changesCount, exitEditMode]);

  const confirmDiscard = useCallback(() => {
    exitEditMode();
  }, [exitEditMode]);

  const abortDiscardConfirmation = useCallback(() => {
    setNeedsDiscardConfirmation(false);
  }, []);

  const setRow = useCallback((month: string, patch: SetRowPatch) => {
    setRows((prev) => {
      const next = new Map(prev);
      const existing = next.get(month);
      if (!existing) return prev;
      next.set(month, { ...existing, ...patch });
      return next;
    });
  }, []);

  const resetRow = useCallback((month: string) => {
    setRows((prev) => {
      const next = new Map(prev);
      const existing = next.get(month);
      if (!existing) return prev;
      next.set(month, { ...existing, interestCents: null, principalCents: null });
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    const errors = new Map<string, string>();
    const upserts: DraftRow[] = [];
    const deletes: DraftRow[] = [];

    for (const draft of rows.values()) {
      const ym = draft.month.slice(0, 7);
      const entry = findEntry(ym, schedule);
      if (!entry) continue;
      const existing = findOverride(ym, overrides);
      const action: DiffAction = computeAction(draft, entry, existing);
      if (action === "NOOP") continue;
      const validation = validateDraftRow(draft);
      if (validation !== "OK") {
        errors.set(ym, validation);
        continue;
      }
      if (action === "UPSERT") upserts.push(draft);
      if (action === "DELETE") deletes.push(draft);
    }

    if (errors.size > 0) {
      setSaveState({ loading: false, rowErrors: errors, lastSaveAttemptCount: 0 });
      return;
    }

    const attemptCount = upserts.length + deletes.length;
    setSaveState({ loading: true, rowErrors: new Map(), lastSaveAttemptCount: attemptCount });

    const results = await Promise.allSettled([
      ...upserts.map((d) =>
        upsertMutation.mutateAsync({
          propertyId,
          loanId,
          input: {
            month: d.month,
            interestCents: d.interestCents,
            principalCents: d.principalCents,
            notes: d.notes,
          },
        }),
      ),
      ...deletes.map((d) =>
        deleteMutation.mutateAsync({
          propertyId,
          loanId,
          overrideId: d.existingOverrideId ?? "",
        }),
      ),
    ]);

    const opOrder = [...upserts, ...deletes];
    const newErrors = new Map<string, string>();
    results.forEach((res, idx) => {
      if (res.status === "rejected") {
        const d = opOrder[idx];
        if (!d) return;
        const msg = res.reason instanceof Error ? res.reason.message : String(res.reason);
        newErrors.set(d.month.slice(0, 7), msg);
      }
    });

    if (newErrors.size === 0) {
      exitEditMode();
      return;
    }

    setSaveState({ loading: false, rowErrors: newErrors, lastSaveAttemptCount: attemptCount });
  }, [rows, schedule, overrides, propertyId, loanId, upsertMutation, deleteMutation, exitEditMode]);

  useEffect(() => {
    if (!editMode || changesCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editMode, changesCount]);

  return {
    editMode,
    enterEditMode,
    cancelEdit,
    confirmDiscard,
    abortDiscardConfirmation,
    needsDiscardConfirmation,
    rows,
    setRow,
    resetRow,
    changesCount,
    save,
    saveState,
  };
}
